import { useState, useEffect } from 'react';
import { Form, Formik, useFormikContext } from 'formik';
import { useHistory } from 'react-router-dom';

import Swal from 'sweetalert2';

import Button from '../../components/Button';
import ButtonBack from '../../components/ButtonBack';
import { FormGroup, FormGroupFlex } from '../../components/Form';
import ErrorMessage from '../../components/Form/ErrorMessage';
import Input from '../../components/Form/Input';
import Loading from '../../components/Loading';
import Spacing from '../../components/Spacing';
import Text from '../../components/Text';
import registerAddressSchema from '../../utils/schemas/registerAddress';

import * as cep from 'cep-promise';

import { Container, TabHeader, FormGroupLine } from './styles';
import { createAddress, login } from '../../services/user';
import ErrorBox from '../../components/ErrorBox';
import m from '../../utils/messages';

const INITIAL_FORM_VALUES = { 
  address_description: '', 
  postal_code: '', 
  district: '', 
  city: '', 
  neighborhood: '',
  address: '', 
  address_number: '',
  address2: '',
};

const RegisterAddress = () => {
  const history = useHistory();
  const locationState = history.location.state || {};

  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (formData, { setSubmitting }) => {
    setSubmitting(true);
    try {
      formData.country = 'BRA';
      formData.address_type = 'delivery';
      formData.landmark = 'N/A';
      formData.postal_code = formData.postal_code.replaceAll('-', '');
  
      const data = await createAddress(formData);
      sessionStorage.removeItem('@createAddressToken@');
      setSuccess(true);
  
      await Swal.fire({
        title: 'Cadastro realizado com sucesso!',
        icon: 'success',
        timer: 5000,
        confirmButtonText: 'OK',
      });
  
      const credentials = {
        email: locationState.email,
        password: locationState.password,
      };
      const loginResponse = await login(credentials);
  
      if (loginResponse?.redirectTo) {
        window.location.href = loginResponse.redirectTo;
      } else {
        history.push('/');
      }
  
    } catch (err) {
      console.log(err);
      setError(m('internalError'));
    }
    setSubmitting(false);
  };

  const handleSkipStep = async () => {
    try {
      const credentials = {
        email: locationState.email,
        password: locationState.password,
      };
      const data = await login(credentials);
      setSuccess(true);

      // Exibe o alerta de sucesso com SweetAlert2
      await Swal.fire({
        title: 'Cadastro realizado com sucesso!',
        icon: 'success',
        timer: 5000,
        confirmButtonText: 'OK',
      });

      // Redireciona após o alerta
      if (data?.redirectTo) {
        window.location.href = data.redirectTo;
      } else {
        history.push('/');
      }
    } catch (err) {
      setError(m('internalError'));
    }
  };

  useEffect(() => {
    if (!sessionStorage.getItem('@createAddressToken@')) {
      history.replace('/login');
    }
  }, [history]);

  return (
    <>
      <ButtonBack onClick={() => history.goBack()} />
      <Container>
        {success && <Loading position="fixed" text="Aguarde" />}
        <TabHeader>
          <Text variant="regular">Minha Conta</Text>
          <Text variant="title1">Endereço</Text>
        </TabHeader>

        <ErrorBox error={error} />
        <Formik 
          initialValues={INITIAL_FORM_VALUES} 
          validationSchema={registerAddressSchema}
          validateOnMount
          onSubmit={handleSubmit}
        >
          {(formikProps) => (
            <>
              <AddressForm {...formikProps} />
              <Spacing size={30} />
              <Button
                variant="buttonSecondary"
                text="Pular etapa"
                onClick={handleSkipStep}
                style={{ width: '100%' }}
              />
            </>
          )}
        </Formik>
        <Text variant="support">
          Ao se cadastrar, você concorda com nossa{' '}
          <a target="_blank" href="https://www.zonasul.com.br/politica-de-privacidade">
            <span className="semibold">
              política de privacidade
            </span>
          </a>
        </Text>
      </Container>
    </>
  );
};

const AddressForm = ({ isSubmitting, isValid, errors }) => {
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [addressFound, setAddressFound] = useState(false);

  const formik = useFormikContext();

  const clearAddress = () => {
    setAddressFound(false);
    formik.setFieldValue('district', '');
    formik.setFieldValue('city', '');
    formik.setFieldValue('neighborhood', '');
    formik.setFieldValue('address', '');
    formik.setFieldValue('address_number', '');
    formik.setFieldValue('address2', '');
  };

  const searchAddressInfo = async (e) => {
    setLoadingSearch(true);

    const searchTerm = e.target.value.replace(/[^0-9]/g, '');

    if (searchTerm.length < 8) {
      clearAddress();
      setLoadingSearch(false);
      return;
    }

    try {
      const result = await cep(searchTerm);
      formik.setFieldValue('district', result.state);
      formik.setFieldValue('city', result.city);
      formik.setFieldValue('neighborhood', result.neighborhood);
      formik.setFieldValue('address', result.street);
      setAddressFound(true);
    } catch (err) {
      clearAddress();
    }

    setLoadingSearch(false);
  };

  return (
    <Form className="form login__form">
      {loadingSearch && <Loading text="Buscando Endereço..." />}
      <FormGroup>
        <Input name="address_description" type="text" label="Nome do Endereço*" autoFocus />
        <ErrorMessage name="address_description" />
      </FormGroup>
      <Spacing size={30} />
      <FormGroupLine>
        <FormGroupFlex flex={2}>
          <Input name="postal_code" mask="DDDDD-DDD" type="text" label="CEP*" onBlur={searchAddressInfo} />
          <ErrorMessage name="postal_code" />
        </FormGroupFlex>
        <FormGroupFlex>
          <Input name="district" type="text" label="UF*" disabled />
          <ErrorMessage name="district" />
        </FormGroupFlex>
      </FormGroupLine>
      <Spacing size={30} />
      <FormGroup>
        <Input name="city" type="text" label="Cidade*" disabled={!addressFound} />
        <ErrorMessage name="city" />
      </FormGroup>
      <Spacing size={30} />
      <FormGroup>
        <Input name="neighborhood" type="text" label="Bairro*" disabled={!addressFound} />
        <ErrorMessage name="neighborhood" />
      </FormGroup>
      <Spacing size={30} />
      <FormGroupLine>
        <FormGroupFlex flex={3}>
          <Input name="address" type="text" label="Endereço*" disabled={!addressFound} />
          <ErrorMessage name="address" />
        </FormGroupFlex>
        <FormGroupFlex>
          <Input name="address_number" type="text" label="Nº*" disabled={!addressFound} />
          <ErrorMessage name="address_number" />
        </FormGroupFlex>
      </FormGroupLine>
      <Spacing size={30} />
      <FormGroup>
        <Input name="address2" type="text" label="Complemento" disabled={!addressFound} />
        <ErrorMessage name="address2" />
      </FormGroup>
      <Spacing size={30} />
      <Button
        variant="buttonPrimary"
        text="Cadastrar"
        type="submit"
        disabled={!isValid}
        loading={isSubmitting}
        style={{ width: '100%' }}
      />
    </Form>
  );
};

export default RegisterAddress;
